import type { PollingOrderStatusType } from '@/types/checkout';
import { type ComputedRef, type Ref } from 'vue';

export function usePollingOrderStatus(
	orderId: ComputedRef<string | null>,
	isEnablePolling: Ref<boolean>,
	callbackSuccess?: () => Promise<void>,
	timeout: number = 60000, // Default timeout set to 60 seconds
	interval: number = 3000, // Default interval set to 3 seconds
) {
	const intervalId = ref<ReturnType<typeof setTimeout>>();
	const timeoutId = ref<ReturnType<typeof setTimeout>>();
	const isProcessing = ref(false);
	const orderStatus = ref<PollingOrderStatusType | null>(null);
	const checkoutStore = useCheckoutStore();
	const notificationStore = useNotificationStore();

	async function clearPolling() {
		clearInterval(intervalId.value);
		clearTimeout(timeoutId.value);

		if (callbackSuccess) {
			await callbackSuccess();
		}
		isProcessing.value = false;
	}

	async function startPolling(id: string) {
		intervalId.value = setInterval(async () => {
			await checkoutStore.getCheckoutOrdersStatusById(id);
		}, interval);

		// Set a timeout to stop polling after the specified time
		timeoutId.value = setTimeout(async () => {
			await clearPolling();
			orderStatus.value = POLLING_ORDER_STATUS.FAIL;
		}, timeout);
	}

	function resetState() {
		if (!isProcessing.value) {
			isProcessing.value = true;
		}

		if (orderStatus) {
			orderStatus.value = null;
		}
	}

	watch([isEnablePolling, orderId], ([newIsEnablePolling, newOrderId]) => {
		if (newIsEnablePolling && newOrderId) {
			resetState();
			startPolling(newOrderId);
		}
	});

	watch(() => checkoutStore.checkoutOrderStatusResponse.errorMessage, async (errorMessage) => {
		if (errorMessage) {
			notificationStore.showErrorNotification(errorMessage);
			await clearPolling();
			orderStatus.value = POLLING_ORDER_STATUS.FAIL;
		}
	});

	watch(() => checkoutStore.checkoutOrderStatusResponse.orderStatus, async (status) => {
		// Stop polling order status interval when status has changed to 'fulfilled'
		if (status && status === CHECKOUT_ORDER_STATUS.FULFILLED) {
			await clearPolling();
			orderStatus.value = POLLING_ORDER_STATUS.SUCCESS;
		}
	});

	return {
		isProcessing,
		orderStatus,
		clearPolling, // Return clearPolling to allow manual stopping of polling if needed
	};
}